// ======================================================================
// 📁 File: DrawingDrawer.jsx
// 🧭 Module: PPE → Engineering → Drawing Master
// 📝 Purpose:
//   - Create / Edit Drawing
//   - Upload drawing file (DRAFT only)
//   - AUTO refresh list & KPIs after save (NO manual refresh needed)
// ======================================================================

import React, { useEffect, useState } from "react";
import { Drawer, Button, Input, Form, Upload, message, Space } from "antd";
import { UploadOutlined } from "@ant-design/icons";

import axiosClient from "../../../../../api/axiosClient";

// ======================================================================
// COMPONENT
// ======================================================================

export default function DrawingDrawer({
  open,
  onClose,
  record,
  onSuccess,
}) {
  const [form] = Form.useForm();
  const isEdit = !!record?.id;

  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  // ----------------------------------------------------------------------
  // Populate form on edit / reset on create
  // ----------------------------------------------------------------------
  useEffect(() => {
    if (!open) return;

    if (record) {
      form.setFieldsValue({
        drawing_no: record.drawing_no,
        revision: record.revision,
        title: record.title,
        description: record.description,
      });
    } else {
      form.resetFields();
    }
  }, [open, record, form]);

  // ----------------------------------------------------------------------
  // SAVE (CREATE / UPDATE) — AUTO REFRESH FIXED
  // ----------------------------------------------------------------------
  const handleSubmit = async (values) => {
    try {
      setSaving(true);

      if (isEdit) {
        // UPDATE
        await axiosClient.put(
          `/api/ppe/masters/drawings/${record.id}`,
          values
        );
        message.success("Drawing updated successfully");
      } else {
        // CREATE
        await axiosClient.post(
          "/api/ppe/masters/drawings",
          values
        );
        message.success("Drawing created successfully");
      }

      // ✅ CRITICAL FIX:
      // Refresh list + KPI BEFORE closing drawer
      if (onSuccess) {
        await onSuccess();
      }

      // Close drawer AFTER data refresh
      onClose();

    } catch (err) {
      message.error(
        err?.response?.data?.message || "Operation failed"
      );
    } finally {
      setSaving(false);
    }
  };

  // ----------------------------------------------------------------------
  // FILE UPLOAD (UNCHANGED, SAFE)
  // ----------------------------------------------------------------------
  const handleUpload = async ({ file }) => {
    try {
      setUploading(true);

      const formData = new FormData();
      formData.append("file", file);

      await axiosClient.post(
        `/api/ppe/masters/drawings/${record.id}/upload`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      message.success("Drawing file uploaded");

      // Refresh list + KPI after upload
      if (onSuccess) {
        await onSuccess();
      }

    } catch (err) {
      message.error(
        err?.response?.data?.message || "Upload failed"
      );
    } finally {
      setUploading(false);
    }
  };

  // ======================================================================
  // RENDER
  // ======================================================================

  return (
    <Drawer
      open={open}
      width={520}
      title={isEdit ? "Edit Drawing" : "Create Drawing"}
      onClose={onClose}
      destroyOnClose
      footer={
        <Space style={{ float: "right" }}>
          <Button onClick={onClose}>
            Cancel
          </Button>

          <Button
            type="primary"
            loading={saving}
            onClick={() => form.submit()}
          >
            {isEdit ? "Update" : "Create"}
          </Button>
        </Space>
      }
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
      >
        <Form.Item
          label="Drawing No"
          name="drawing_no"
          rules={[
            { required: true, message: "Drawing No is required" },
          ]}
        >
          <Input disabled={isEdit} />
        </Form.Item>

        <Form.Item
          label="Revision"
          name="revision"
          rules={[
            { required: true, message: "Revision is required" },
          ]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          label="Title"
          name="title"
          rules={[
            { required: true, message: "Title is required" },
          ]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          label="Description"
          name="description"
        >
          <Input.TextArea rows={3} />
        </Form.Item>
      </Form>

      {/* =============================================================== */}
      {/* FILE UPLOAD (EDIT + DRAFT ONLY)                                  */}
      {/* =============================================================== */}
      {/* =============================================================== */}
{/* FILE UPLOAD (EDIT + DRAFT ONLY)                                  */}
{/* =============================================================== */}
{isEdit && record?.status_code === "DRAFT" && (
  <>
    <div style={{ marginTop: 24, fontWeight: 600 }}>
      Drawing File
    </div>

    <Upload
      customRequest={handleUpload}
      showUploadList={false}
      accept=".pdf,.dwg,.dxf"
    >
      <Button
        icon={<UploadOutlined />}
        loading={uploading}
        style={{ marginTop: 8 }}
      >
        Upload / Replace File
      </Button>
    </Upload>
  </>
)}

    </Drawer>
  );
}
